import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, CheckCircle2, ChevronDown, ChevronUp, Gauge, Hash, Lock, Plus, Save, Send, ShieldCheck, XCircle, Zap } from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Campo, Cartao, CorpoCartao, Pilula } from '@/componentes/ui/primitivos';
import type { Destino, LinhaTributacao } from './tipos';

/**
 * O cadastro da esteira, num lugar só e com dono (pedido do documento do PH:
 * "novo cliente ou novo destino entra por cadastro, sem mexer em código").
 *  - destinos: operação × destino × empresa × centros × série (MEG-870);
 *  - Slack: quem é quem, para marcar o comprador e deixar a resposta agir;
 *  - regras: tolerâncias, limite de DOH, canais, lançamento automático.
 */
export function Cadastro(): JSX.Element {
  return (
    <div className="space-y-5">
      <ConexaoSlack />
      <Destinos />
      <TributacaoTransferencia />
      <div className="grid gap-5 lg:grid-cols-2">
        <Regras />
        <SlackUsuarios />
      </div>
    </div>
  );
}

function Destinos(): JSX.Element {
  const cliente = useQueryClient();
  const consulta = useQuery<{ destinos: Destino[] }>({ queryKey: ['entradas-destinos'], queryFn: () => api.get('/api/entradas/destinos') });
  const salvar = useMutation({
    mutationFn: (d: Destino) => api.put('/api/entradas/destinos', d),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['entradas-destinos'] }),
  });
  const [edicao, definirEdicao] = React.useState<Record<number, Partial<Destino>>>({});
  const mudar = (id: number, campo: keyof Destino, valor: unknown): void =>
    definirEdicao((e) => ({ ...e, [id]: { ...e[id], [campo]: valor } }));
  const num = (v: string): number | null => (v.trim() ? Number(v.replace(/\D/g, '')) : null);
  const [novo, definirNovo] = React.useState(false);

  return (
    <Cartao className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-borda px-4 py-3">
        <div>
          <p className="text-sm font-bold">Destinos</p>
          <p className="text-xs text-textoFraco">
            Para onde a logística pode mandar a carga. Os centros são conferidos contra a empresa ao salvar. Destino de outro CNPJ: série, centro de
            saída e operações da NF de transferência (vazio = o padrão das transferências reais: série 57, centro 1104, operações 5 → 42).
          </p>
        </div>
        {novo ? null : (
          <Botao tamanho="pequeno" variante="secundaria" onClick={() => definirNovo(true)}>
            <Plus className="h-4 w-4" /> Novo destino
          </Botao>
        )}
      </div>
      {novo ? <NovoDestino aoFechar={() => definirNovo(false)} /> : null}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-superficie2 text-left text-textoFraco">
            <tr>
              {['Destino', 'Tipo', 'Empresa', 'Recebimento', 'Venda', 'Estoque transitório', 'Série transf.', 'Sai do centro', 'Op. saída → entrada', 'Ativo', ''].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 font-semibold"
                  title={
                    h === 'Estoque transitório'
                      ? 'Onde a entrada da NF de transferência fica até chegar: armazém no xx02 (Recebimento de transferências — 3202), Full no xx04 (1504). Na chegada, a cega leva para o Recebimento; no Full, a transferência leva para a Venda.'
                      : h === 'Sai do centro'
                      ? 'Centro da empresa da compra de onde a NF de transferência baixa o estoque (1104 = Reserva Full)'
                      : h === 'Op. saída → entrada'
                        ? 'Operações fiscais da NF de transferência: saída na empresa da compra, entrada no destino'
                        : undefined
                  }
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-borda">
            {(consulta.data?.destinos ?? []).map((d) => {
              const e = { ...d, ...edicao[d.id] };
              const sujo = Boolean(edicao[d.id]);
              return (
                <tr key={d.id}>
                  <td className="px-3 py-1.5">
                    <b>{d.nome}</b> {d.uf}
                    <span className="block text-textoSuave">{d.operacao}</span>
                  </td>
                  <td className="px-3 py-1.5">
                    <Pilula tom={d.tipo === 'FULL' ? 'alerta' : 'marca'}>{d.tipo === 'FULL' ? 'Full' : 'Armazém'}</Pilula>
                  </td>
                  <td className="px-3 py-1.5 tabular">{d.empresa}</td>
                  {(['centroRecebimento', 'centroVenda', 'centroTransito'] as const).map((c) => (
                    <td key={c} className="px-2 py-1.5">
                      <Campo className="h-8 w-20 text-xs" value={e[c] ?? ''} onChange={(ev) => mudar(d.id, c, num(ev.target.value))} />
                    </td>
                  ))}
                  <td className="px-2 py-1.5">
                    <Campo className="h-8 w-16 text-xs" value={e.serieTransferencia ?? ''} onChange={(ev) => mudar(d.id, 'serieTransferencia', ev.target.value || null)} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Campo className="h-8 w-20 text-xs" placeholder="1104" value={e.centroSaidaTransf ?? ''} onChange={(ev) => mudar(d.id, 'centroSaidaTransf', num(ev.target.value))} />
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="flex items-center gap-1">
                      <Campo className="h-8 w-12 text-xs" placeholder="5" value={e.operacaoSaidaTransf ?? ''} onChange={(ev) => mudar(d.id, 'operacaoSaidaTransf', num(ev.target.value))} />
                      <span className="text-textoSuave">→</span>
                      <Campo className="h-8 w-12 text-xs" placeholder="42" value={e.operacaoEntradaTransf ?? ''} onChange={(ev) => mudar(d.id, 'operacaoEntradaTransf', num(ev.target.value))} />
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    <input type="checkbox" checked={e.ativo} onChange={(ev) => mudar(d.id, 'ativo', ev.target.checked)} />
                  </td>
                  <td className="px-3 py-1.5">
                    {sujo ? (
                      <Botao
                        tamanho="micro"
                        variante="primaria"
                        carregando={salvar.isPending}
                        onClick={() =>
                          salvar.mutate(e as Destino, {
                            onSuccess: () => definirEdicao(({ [d.id]: _, ...resto }) => resto),
                          })
                        }
                      >
                        <Save className="h-3.5 w-3.5" />
                      </Botao>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {salvar.error ? <p className="px-4 py-2 text-xs text-status-divergencia">{salvar.error instanceof ErroHttp ? salvar.error.message : String(salvar.error)}</p> : null}
    </Cartao>
  );
}

/**
 * Um destino novo (armazém ou Full): nome, operação, UF, empresa e os centros.
 * Armazém recebe no centro de recebimento (a cega sai nele) e vende no de
 * venda; Full vai para o centro de trânsito e, confirmada a chegada, para o de
 * venda. A API confere que todo centro existe e é da empresa.
 */
function NovoDestino({ aoFechar }: { aoFechar: () => void }): JSX.Element {
  const cliente = useQueryClient();
  const [f, definirF] = React.useState({
    nome: '',
    operacao: '',
    uf: '',
    tipo: 'ARMAZEM' as 'ARMAZEM' | 'FULL',
    empresa: '',
    centroRecebimento: '',
    centroVenda: '',
    centroTransito: '',
    serieTransferencia: '',
  });
  const mudar = (k: keyof typeof f, v: string): void => definirF((o) => ({ ...o, [k]: v }));
  const n = (v: string): number | null => (v.trim() ? Number(v.replace(/\D/g, '')) : null);
  const full = f.tipo === 'FULL';
  const completo =
    f.nome.trim() && f.operacao.trim() && f.uf.trim().length === 2 && n(f.empresa) && n(f.centroVenda) && n(f.centroTransito) && (full || n(f.centroRecebimento));
  const salvar = useMutation({
    mutationFn: () =>
      api.put('/api/entradas/destinos', {
        nome: f.nome.trim(),
        operacao: f.operacao.trim(),
        uf: f.uf.trim(),
        tipo: f.tipo,
        empresa: n(f.empresa),
        centroRecebimento: full ? null : n(f.centroRecebimento),
        centroVenda: n(f.centroVenda),
        centroTransito: n(f.centroTransito),
        serieTransferencia: f.serieTransferencia.trim() || null,
        ativo: true,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['entradas-destinos'] });
      aoFechar();
    },
  });
  const campo = (k: keyof typeof f, rotulo: string, ajuda: string, props: React.ComponentProps<typeof Campo> = {}) => (
    <label className="block space-y-0.5">
      <span className="text-xs font-semibold">{rotulo}</span>
      <Campo value={f[k]} onChange={(e) => mudar(k, e.target.value)} {...props} />
      <span className="text-2xs text-textoSuave">{ajuda}</span>
    </label>
  );
  return (
    <div className="space-y-3 border-b border-borda bg-primaria-suave/20 px-4 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold">Tipo:</span>
        {(['ARMAZEM', 'FULL'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => mudar('tipo', t)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-semibold',
              f.tipo === t ? 'border-primaria bg-primaria text-white' : 'border-borda bg-superficie text-textoFraco',
            )}
          >
            {t === 'ARMAZEM' ? 'Armazém (recebe e confere com cega)' : 'Full (marketplace: trânsito → venda)'}
          </button>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {campo('nome', 'Nome', 'Como aparece para a logística. Ex.: Mercocamp ES')}
        {campo('operacao', 'Operação', 'Sigla curta. Ex.: MERCOCAMP, FULL_ML')}
        {campo('uf', 'UF', 'Onde fica. Ex.: ES', { maxLength: 2 })}
        {campo('empresa', 'Empresa (ERP)', 'O CNPJ que recebe a carga. Ex.: 11', { inputMode: 'numeric' })}
        {campo('centroTransito', 'Estoque transitório', full ? 'Onde a entrada fica até chegar no Full (o 04, ex.: 1504).' : 'Onde a entrada fica até chegar no CD (o 02, ex.: 3202).', { inputMode: 'numeric' })}
        {full ? null : campo('centroRecebimento', 'Centro de recebimento', 'Onde a cega é gerada e conferida.', { inputMode: 'numeric' })}
        {campo('centroVenda', 'Centro de venda', 'Para onde vai depois de conferido/chegado.', { inputMode: 'numeric' })}
        {campo('serieTransferencia', 'Série da transferência', 'Se a empresa não for a 11. Vazio = o fiscal define depois.')}
      </div>
      {salvar.error ? <p className="text-xs text-status-divergencia">{salvar.error instanceof ErroHttp ? salvar.error.message : String(salvar.error)}</p> : null}
      <div className="flex gap-2">
        <Botao variante="primaria" disabled={!completo} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
          <Save className="h-4 w-4" /> Criar destino
        </Botao>
        <Botao variante="fantasma" onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>
    </div>
  );
}

const brl = (v: number): string => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Liga/desliga no lugar de "1/0". */
function Interruptor({ ligado, aoMudar, rotulo }: { ligado: boolean; aoMudar: (v: boolean) => void; rotulo: string }): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      aria-label={rotulo}
      onClick={() => aoMudar(!ligado)}
      className={cn('relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition', ligado ? 'bg-status-livre' : 'bg-bordaForte')}
    >
      <span className={cn('inline-block h-5 w-5 rounded-full bg-white shadow transition', ligado ? 'translate-x-5' : 'translate-x-0.5')} />
    </button>
  );
}

/**
 * Um grupo de regras (automações, aprovações, limites, avisos): cabeçalho com
 * ícone, o que o grupo decide e quantas regras tem; recolhe e expande (abre aberto).
 */
function GrupoRegras({
  titulo,
  explica,
  icone: Icone,
  regras,
  children,
}: {
  titulo: string;
  explica: string;
  icone: React.ElementType;
  regras: number;
  children: React.ReactNode;
}): JSX.Element {
  const [aberto, definirAberto] = React.useState(true);
  return (
    <section className="overflow-hidden rounded-xl border border-borda">
      <button
        type="button"
        onClick={() => definirAberto((x) => !x)}
        aria-expanded={aberto}
        className="flex w-full items-center gap-3 bg-superficie2/60 px-4 py-3 text-left transition hover:bg-superficie2"
      >
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primaria-suave text-primaria">
          <Icone className="h-[18px] w-[18px]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold text-texto">{titulo}</span>
          <span className="block text-xs text-textoFraco">{explica}</span>
        </span>
        <span className="shrink-0 rounded-full border border-borda bg-superficie px-2 py-0.5 text-2xs font-semibold text-textoFraco">
          {regras} {regras === 1 ? 'regra' : 'regras'}
        </span>
        {aberto ? <ChevronUp className="h-4 w-4 shrink-0 text-textoSuave" /> : <ChevronDown className="h-4 w-4 shrink-0 text-textoSuave" />}
      </button>
      {aberto ? <div className="divide-y divide-borda px-4">{children}</div> : null}
    </section>
  );
}

function Regra({ titulo, children, controle }: { titulo: string; children: React.ReactNode; controle: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0 space-y-1">
        <p className="text-[13px] font-bold">{titulo}</p>
        <div className="space-y-1 text-xs leading-relaxed text-textoFraco">{children}</div>
      </div>
      <div className="shrink-0">{controle}</div>
    </div>
  );
}

function CampoNumero({ valor, aoMudar, sufixo }: { valor: string; aoMudar: (v: string) => void; sufixo: string }): JSX.Element {
  return (
    <div className="flex items-center gap-1.5">
      <Campo className="h-9 w-20 text-right" inputMode="decimal" value={valor} onChange={(e) => aoMudar(e.target.value.replace(/[^\d.,]/g, ''))} />
      <span className="text-xs font-semibold text-textoFraco">{sufixo}</span>
    </div>
  );
}

/**
 * As regras da esteira em português: cada uma diz o que faz, o que acontece
 * ligada e desligada, e um exemplo com o número que está na tela. Em
 * 27/10/2026 o cartão era "Captura ligada (1/0)" e "Tolerância de preço (%)",
 * e quem abriu perguntou o que era cada coisa.
 */
function Regras(): JSX.Element {
  const consulta = useQuery<{ config: Record<string, string>; slack: boolean }>({ queryKey: ['entradas-config'], queryFn: () => api.get('/api/entradas/config') });
  const [valores, definirValores] = React.useState<Record<string, string>>({});
  React.useEffect(() => {
    if (consulta.data) definirValores(consulta.data.config);
  }, [consulta.data]);
  const salvar = useMutation({ mutationFn: () => api.put('/api/entradas/config', valores), onSuccess: () => void consulta.refetch() });
  const v = (k: string): string => valores[k] ?? '';
  const ligado = (k: string): boolean => ['1', 'true', 'sim', 'S'].includes(v(k).trim());
  const definir = (k: string, x: string): void => definirValores((o) => ({ ...o, [k]: x }));
  const numero = (k: string): number => Number(v(k).replace(',', '.')) || 0;
  const sujo = consulta.data ? Object.keys(valores).some((k) => valores[k] !== consulta.data!.config[k]) : false;

  const tolPreco = numero('entradas.tolerancia_preco');
  const tolQtd = numero('entradas.tolerancia_qtd');
  const doh = numero('entradas.doh_limite');

  return (
    <Cartao>
      <CorpoCartao className="space-y-1">
        <div className="flex items-center justify-between pb-1">
          <p className="text-sm font-bold">Regras da esteira</p>
          <Pilula tom={consulta.data?.slack ? 'ok' : 'neutro'}>{consulta.data?.slack ? 'Slack conectado' : 'Slack sem token'}</Pilula>
        </div>

        <div className="space-y-3">
          <GrupoRegras titulo="Automações" explica="O que o WMS faz sozinho, sem ninguém clicar." icone={Zap} regras={6}>
            <Regra
              titulo="Trazer as notas do portal fiscal"
              controle={<Interruptor rotulo="Trazer as notas" ligado={ligado('entradas.captura_ativa')} aoMudar={(x) => definir('entradas.captura_ativa', x ? '1' : '0')} />}
            >
              <p>
                <b>Ligado:</b> a cada 10 min o WMS pega no portal fiscal as NF-e de fornecedor emitidas contra nós e põe na esteira.
              </p>
              <p>
                <b>Desligado:</b> nenhuma nota nova entra. As que já estão continuam andando.
              </p>
            </Regra>

            <Regra
              titulo="Dar a entrada fiscal sozinho"
              controle={<Interruptor rotulo="Entrada fiscal automática" ligado={ligado('entradas.lancar_auto')} aoMudar={(x) => definir('entradas.lancar_auto', x ? '1' : '0')} />}
            >
              <p>
                <b>Ligado:</b> nota que bateu com a OC já vira NF de compra no Procfit (a mesma inserção do botão “Dar entrada fiscal”), sem o fiscal
                clicar. Sem estoque: o estoque só entra com a cega.
              </p>
              <p>
                <b>Desligado:</b> a nota fica em “Validada” até o fiscal clicar.
              </p>
            </Regra>

            <Regra
              titulo="Validar a compra no ERP junto com a entrada"
              controle={<Interruptor rotulo="Validar a compra" ligado={ligado('entradas.validar_compra_auto')} aoMudar={(x) => definir('entradas.validar_compra_auto', x ? '1' : '0')} />}
            >
              <p>
                <b>Ligado:</b> a entrada automática já sobe validada, como salvar no Procfit: título a pagar pelas parcelas da OC e estoque no centro de
                saída (1104). Se algo impedir (sem parcela, período fechado), a nota fica como pré-entrada e o roteiro mostra o porquê.
              </p>
              <p>
                <b>Desligado:</b> sobe como pré-entrada (sem título e sem estoque) e o fiscal valida pelo botão “Validar no ERP” do roteiro.
              </p>
            </Regra>

            <Regra
              titulo="Gerar a cega junto com a entrada fiscal"
              controle={<Interruptor rotulo="Cega junto com a entrada" ligado={ligado('entradas.cega_junto_entrada')} aoMudar={(x) => definir('entradas.cega_junto_entrada', x ? '1' : '0')} />}
            >
              <p>
                <b>Ligado:</b> gerou a NF de compra (pelo botão ou pela entrada automática), gera o recebimento logo em seguida, no armazém da empresa
                da nota, e a nota vai direto para Em conferência. Na entrada automática, quem assina a cega é quem ligou esta regra.
              </p>
              <p>
                <b>Desligado:</b> quem dá a entrada escolhe, nota a nota, marcando “Gerar a cega junto”. Sem marcar, a cega sai na chegada.
              </p>
              <p className="text-textoSuave">Só para armazém da mesma empresa: Luft, Lisa e Full seguem pela NF de transferência.</p>
            </Regra>

            <Regra
              titulo="Emitir a NF de transferência pelo WMS"
              controle={<Interruptor rotulo="NF de transferência pelo WMS" ligado={ligado('entradas.transf_gerar')} aoMudar={(x) => definir('entradas.transf_gerar', x ? '1' : '0')} />}
            >
              <p>
                <b>Ligado:</b> nota agendada para outro CNPJ (Meli Full MG, Luft, Lisa…) mostra a NF de transferência já montada: itens da compra, valor
                (compra + IPI), CST e ICMS do grupo tributário. O fiscal confere, ajusta se quiser (nota parcial, valor) e emite com um clique. Autorizada, o WMS
                dá a entrada no destino no estoque transitório (Lisa 3202, Full 04), como o fiscal faz no Procfit, e vincula. Na chegada: cega (armazém) ou 04 → 01 (Full).
              </p>
              <p>
                <b>Desligado:</b> como hoje — emite no Procfit e vincula a entrada na nota.
              </p>
              {ligado('entradas.transf_gerar') ? (
                <div className="mt-2 space-y-2 rounded-lg border border-borda p-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <span>
                      <b className="text-texto">Emitir sozinho ao agendar</b>
                      <span className="block">Nota inteira, assinada por quem agendou. Se o PBS recusar, fica na linha do tempo e o botão continua lá.</span>
                    </span>
                    <Interruptor rotulo="Emitir ao agendar" ligado={ligado('entradas.transf_auto')} aoMudar={(x) => definir('entradas.transf_auto', x ? '1' : '0')} />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span>
                      <b className="text-texto">Valor unitário padrão</b>
                      <span className="block">46 de 60 NFs recentes usaram compra + IPI.</span>
                    </span>
                    <select
                      className="h-9 rounded-lg border border-borda bg-superficie px-2 text-xs"
                      aria-label="Valor unitário padrão"
                      value={v('entradas.transf_valor_base') || 'compra_ipi'}
                      onChange={(e) => definir('entradas.transf_valor_base', e.target.value)}
                    >
                      <option value="compra_ipi">Compra + IPI</option>
                      <option value="compra">Só a compra</option>
                    </select>
                  </div>
                </div>
              ) : null}
            </Regra>

            <Regra
              titulo="Gravar o de-para sozinho pelo EAN"
              controle={<Interruptor rotulo="De-para automático" ligado={ligado('entradas.depara_auto_ean')} aoMudar={(x) => definir('entradas.depara_auto_ean', x ? '1' : '0')} />}
            >
              <p>
                <b>Ligado:</b> quando o EAN do XML acha o produto e o código do fornecedor não tem de-para, o WMS grava o de-para no cadastro do produto
                (aba Fornecedores) e registra na linha do tempo.
              </p>
              <p>
                <b>Desligado:</b> só sugere; alguém confirma na gaveta. Ligue quando o cadastro de EAN estiver confiável: EAN errado vira de-para errado.
              </p>
            </Regra>

            <Regra
              titulo="Manter a OC do XML mesmo sem os itens"
              controle={<Interruptor rotulo="Manter a OC do XML" ligado={ligado('entradas.xped_manter_do_xml')} aoMudar={(x) => definir('entradas.xped_manter_do_xml', x ? '1' : '0')} />}
            >
              <p>
                <b>Desligado (padrão):</b> se a OC do XML não tem nenhum item da nota e uma única OC aberta do fornecedor tem todos, o WMS usa essa e
                registra na linha do tempo.
              </p>
              <p>
                <b>Ligado:</b> fica a OC do XML, com "item fora da OC" e a OC sugerida à vista; o comprador decide.
              </p>
            </Regra>
          </GrupoRegras>
          <GrupoRegras titulo="Aprovações e travas" explica="Quando a nota trava ou precisa de alguém para seguir." icone={ShieldCheck} regras={5}>
            <Regra
              titulo="Agendamento acima do DOH só com aprovação no Slack"
              controle={
                <Interruptor
                  rotulo="Acima do DOH só com aprovação no Slack"
                  ligado={ligado('entradas.doh_exige_aprovacao_slack')}
                  aoMudar={(x) => definir('entradas.doh_exige_aprovacao_slack', x ? '1' : '0')}
                />
              }
            >
              <p>
                <b>Ligado:</b> ninguém agenda acima do DOH pelo WMS. A tentativa vira um pedido no canal de compras, com as notas e os itens acima do
                limite, marcando quem está com “Aprova DOH” em Pessoas do Slack. Um deles clica Aprovar (o WMS agenda e avisa quem pediu) ou Recusar
                (não agenda). Tudo vai para a linha do tempo da nota.
              </p>
              <p>
                <b>Desligado:</b> como hoje — quem tem a permissão “liberar DOH” agenda com uma justificativa.
              </p>
            </Regra>

            <Regra
              titulo="Produto sem histórico de venda no destino"
              controle={
                <Interruptor
                  rotulo="Sem histórico agenda direto"
                  ligado={ligado('entradas.doh_sem_historico_libera')}
                  aoMudar={(x) => definir('entradas.doh_sem_historico_libera', x ? '1' : '0')}
                />
              }
            >
              <p>
                Produto que nunca vendeu saindo do destino não tem como medir o DOH (é o caso de lançamento ou de destino novo).
              </p>
              <p>
                <b>Desligado (padrão):</b> trava como o DOH acima do limite — segue a regra de cima: pedido para quem aprova DOH no Slack, ou
                justificativa de quem tem “liberar DOH”.
              </p>
              <p>
                <b>Ligado:</b> agenda direto. O aviso “sem histórico” continua na tela e fica na linha do tempo da nota. Item acima do limite continua
                travando.
              </p>
            </Regra>

            <Regra
              titulo="Agendar nota com divergência só com aprovação"
              controle={
                <Interruptor
                  rotulo="Exigir aprovação"
                  ligado={ligado('entradas.agendar_divergente_exige_aprovacao')}
                  aoMudar={(x) => definir('entradas.agendar_divergente_exige_aprovacao', x ? '1' : '0')}
                />
              }
            >
              <p>
                <b>Ligado:</b> nota com divergência (OC × pré-pedido, NFs acima da OC ou divergência da nota) só agenda com aprovação no Slack de quem
                aprova DOH. Aprovado, o WMS agenda sozinho.
              </p>
              <p>
                <b>Desligado:</b> quem agenda escolhe seguir (fica na linha do tempo) ou pedir aprovação.
              </p>
            </Regra>

            <Regra
              titulo="Não agendar nota sem OC"
              controle={
                <Interruptor rotulo="Travar sem OC" ligado={ligado('entradas.agendar_sem_oc_bloqueia')} aoMudar={(x) => definir('entradas.agendar_sem_oc_bloqueia', x ? '1' : '0')} />
              }
            >
              <p>
                <b>Ligado:</b> nota sem OC não agenda: o comprador indica a OC antes.
              </p>
              <p>
                <b>Desligado:</b> agenda com aviso, e a linha do tempo registra quem agendou sem OC.
              </p>
            </Regra>

            <Regra
              titulo="Limite de aceite do comprador"
              controle={
                <div className="flex items-center gap-2">
                  <CampoNumero valor={v('entradas.aceite_limite_pct')} aoMudar={(x) => definir('entradas.aceite_limite_pct', x)} sufixo="%" />
                  <CampoNumero valor={v('entradas.aceite_limite_valor')} aoMudar={(x) => definir('entradas.aceite_limite_valor', x)} sufixo="R$" />
                </div>
              }
            >
              <p>
                Quanto o comprador (ou o fiscal) aceita de divergência sem precisar de quem aprova DOH: a diferença em R$ (preço a mais, quantidade
                acima do saldo, item fora do pré-pedido) em % do valor da nota ou em reais. 0 = sem limite.
              </p>
              <p className="text-textoSuave">
                Ex.: {numero('entradas.aceite_limite_pct') || '—'}% / {numero('entradas.aceite_limite_valor') ? brl(numero('entradas.aceite_limite_valor')) : '—'}: passou de
                qualquer um, o "Aceitar" pede autorização no Slack.
              </p>
            </Regra>
          </GrupoRegras>
          <GrupoRegras titulo="Limites e tolerâncias" explica="Até onde a diferença ainda não é divergência." icone={Gauge} regras={4}>
            <Regra titulo="Preço acima da OC que ainda passa" controle={<CampoNumero valor={v('entradas.tolerancia_preco')} aoMudar={(x) => definir('entradas.tolerancia_preco', x)} sufixo="%" />}>
              <p>
                Compara o preço por unidade de cada item da nota com o da OC. Só olha para <b>cima</b>: nota mais barata que a OC sempre passa.
              </p>
              <p className="text-textoSuave">
                Ex.: item a {brl(100)} na OC → a nota passa até <b>{brl(100 * (1 + tolPreco / 100))}</b>. Acima disso, vai para Divergente.
              </p>
            </Regra>

            <Regra titulo="Quantidade acima do saldo que ainda passa" controle={<CampoNumero valor={v('entradas.tolerancia_qtd')} aoMudar={(x) => definir('entradas.tolerancia_qtd', x)} sufixo="%" />}>
              <p>
                Compara a quantidade da nota com o <b>saldo</b> da OC (o pedido menos o que outras notas da mesma OC já trouxeram). Só olha para{' '}
                <b>cima</b>: menos que o saldo sempre passa (entrega parcial).
              </p>
              <p className="text-textoSuave">
                Ex.: saldo de 100 un. → a nota passa até <b>{Math.floor(100 * (1 + tolQtd / 100))} un.</b>
                {tolQtd === 0 ? ' (0% = nem uma unidade a mais).' : '.'}
              </p>
            </Regra>

            <Regra
              titulo="NFs acima da OC que ainda passam"
              controle={<CampoNumero valor={v('entradas.oc_acima_tolerancia_pct')} aoMudar={(x) => definir('entradas.oc_acima_tolerancia_pct', x)} sufixo="%" />}
            >
              <p>Na grade por OC, o cabeçalho fica vermelho quando a soma das NFs passa do valor da OC. Frete e impostos na NF podem passar um pouco.</p>
              <p className="text-textoSuave">
                Ex.: OC de {brl(100000)} → vermelho acima de <b>{brl(100000 * (1 + numero('entradas.oc_acima_tolerancia_pct') / 100))}</b>.
              </p>
            </Regra>

            <Regra titulo="Cobertura máxima depois de receber (DOH)" controle={<CampoNumero valor={v('entradas.doh_limite')} aoMudar={(x) => definir('entradas.doh_limite', x)} sufixo="dias" />}>
              <p>
                Na hora de agendar, o WMS calcula para cada item, <b>no destino escolhido</b>: <b>(estoque de hoje nos centros do destino + o que a
                nota traz) ÷ venda por dia que saiu do centro de venda dele</b> — ex.: Meli Full MG = 1501 (+ transitório 1504), sem somar o 2301, o 3201
                e os outros. A venda por dia é a média dos últimos 90 dias — ou dos últimos 30, se o ritmo mudou 30% ou mais.
              </p>
              <p>
                Se algum item passar de <b>{doh || '—'} dias</b>, o agendamento trava e só quem tem a permissão “liberar DOH” libera, com motivo.
              </p>
              <p className="text-textoSuave">
                Item sem venda nos últimos 90 dias, mas que já vendeu antes: vale o ritmo dos 90 dias até a última venda (a tela mostra o período).
                Produto que nunca vendeu não tem DOH: não trava, e o agendamento anota isso na linha do tempo da nota.
              </p>
            </Regra>
          </GrupoRegras>
          <GrupoRegras titulo="Avisos no Slack" explica="O que o WMS manda sozinho no Slack, pelo app, e para quem." icone={BellRing} regras={13}>
            <Regra
              titulo="Avisar no Slack sozinho"
              controle={<Interruptor rotulo="Avisar no Slack sozinho" ligado={ligado('entradas.slack_avisar_auto')} aoMudar={(x) => definir('entradas.slack_avisar_auto', x ? '1' : '0')} />}
            >
              <p>
                <b>Ligado:</b> quando uma nota cai em Sem OC ou Divergente (e nas outras etapas que pedem gente), o WMS abre a thread dela no canal e
                marca o comprador da OC.
              </p>
              <p>
                <b>Desligado:</b> nada sai sozinho; o Slack só recebe o que alguém escreve na nota, as respostas do bot e o aviso de recusa.
              </p>
            </Regra>

            <Regra
              titulo="Slack: agendado acima do DOH"
              controle={<Interruptor rotulo="Avisar agendamento acima do DOH" ligado={ligado('entradas.slack_avisar_doh')} aoMudar={(x) => definir('entradas.slack_avisar_doh', x ? '1' : '0')} />}
            >
              <p>
                Quando alguém com “liberar DOH” agenda acima do limite, o canal de compras recebe a nota, o pior item, os dias de cobertura, quem liberou
                e o motivo, marcando o comprador.
              </p>
            </Regra>

            <Regra
              titulo="Slack: agendada e não chegou"
              controle={<Interruptor rotulo="Avisar agendada que não chegou" ligado={ligado('entradas.slack_avisar_atraso')} aoMudar={(x) => definir('entradas.slack_avisar_atraso', x ? '1' : '0')} />}
            >
              <p>
                Passaram 2 h do fim da janela (ou das 18 h, sem janela) e a nota continua em Agendada: o canal da logística recebe o agendamento,
                marcando o comprador. Uma vez por agendamento, das 8 h às 18 h (horário de Brasília), olhando os últimos 7 dias.
              </p>
            </Regra>

            <Regra
              titulo="Slack: nota entrou no ERP com divergência da OC"
              controle={
                <Interruptor
                  rotulo="Avisar entrada com divergência"
                  ligado={ligado('entradas.slack_avisar_divergencia')}
                  aoMudar={(x) => definir('entradas.slack_avisar_divergencia', x ? '1' : '0')}
                />
              }
            >
              <p>
                <b>Ligado:</b> quando a entrada é feita no ERP com preço, quantidade ou item fora da OC, o WMS posta sozinho (pelo app) no canal,
                marcando o comprador.
              </p>
              <p>
                <b>Desligado:</b> a nota ganha o selo “entrou com divergência” e quem abrir avisa pela gaveta — “Avisar no Slack” sai pelo Slack da
                pessoa, que escolhe quem marcar.
              </p>
            </Regra>

            <Regra
              titulo="Slack: entrada fiscal automática falhou"
              controle={<Interruptor rotulo="Avisar falha da entrada automática" ligado={ligado('entradas.slack_avisar_falha')} aoMudar={(x) => definir('entradas.slack_avisar_falha', x ? '1' : '0')} />}
            >
              <p>
                Com “Dar a entrada fiscal sozinho” ligado, se o ERP recusar a entrada (ou a cega que sai junto), o canal fiscal recebe a nota e o motivo.
                Sem isto, a nota só fica parada em Validada.
              </p>
            </Regra>

            <Regra
              titulo="Slack: captura de notas parada"
              controle={<Interruptor rotulo="Avisar captura parada" ligado={ligado('entradas.slack_avisar_captura')} aoMudar={(x) => definir('entradas.slack_avisar_captura', x ? '1' : '0')} />}
            >
              <p>
                O portal fiscal recusou ou não respondeu por 30 min seguidos: o canal fiscal recebe um aviso, e outro quando voltar. Enquanto isso,
                nenhuma NF nova entra na esteira.
              </p>
            </Regra>
            <Regra titulo="Slack: resumo diário por comprador" controle={<div className="flex items-center gap-3"><CampoNumero valor={v('entradas.slack_resumo_hora')} aoMudar={(x) => definir('entradas.slack_resumo_hora', x)} sufixo="h" /><Interruptor rotulo="Resumo diário" ligado={ligado('entradas.slack_resumo_diario')} aoMudar={(x) => definir('entradas.slack_resumo_diario', x ? '1' : '0')} /></div>}>
              <p>
                <b>Ligado:</b> uma vez por dia (a partir da hora ao lado), cada comprador recebe um resumo: as notas divergentes ou sem OC com ele, as OCs diferentes do pré-pedido e as NFs acima do valor da OC.
              </p>
              <p>
                <b>Desligado:</b> sem resumo; os avisos soltos continuam conforme as outras chaves.
              </p>
            </Regra>

            <Regra titulo="Slack: OC divergente do pré-pedido" controle={<Interruptor rotulo="Avisar OC × pré-pedido" ligado={ligado('entradas.slack_avisar_oc_pre')} aoMudar={(x) => definir('entradas.slack_avisar_oc_pre', x ? '1' : '0')} />}>
              <p>
                <b>Ligado:</b> quando a OC de uma nota aberta difere do pré-pedido aprovado (quantidade, valor, SKU fora), o comprador é marcado com o que difere. Uma vez por OC.
              </p>
              <p>
                <b>Desligado:</b> a diferença só aparece na grade e na gaveta.
              </p>
            </Regra>

            <Regra titulo="Slack: NFs acima do valor da OC" controle={<Interruptor rotulo="Avisar NFs acima da OC" ligado={ligado('entradas.slack_avisar_oc_acima')} aoMudar={(x) => definir('entradas.slack_avisar_oc_acima', x ? '1' : '0')} />}>
              <p>
                <b>Ligado:</b> quando a soma das NFs de uma OC passa do valor dela (com a tolerância de "NFs acima da OC"), o comprador é marcado com o valor da OC e o das NFs. Uma vez por OC.
              </p>
              <p>
                <b>Desligado:</b> só o cabeçalho vermelho na grade e o botão "Avisar comprador".
              </p>
            </Regra>

            <Regra titulo="Slack: contagem da cega diferente da nota" controle={<Interruptor rotulo="Avisar contagem diferente" ligado={ligado('entradas.slack_avisar_contagem')} aoMudar={(x) => definir('entradas.slack_avisar_contagem', x ? '1' : '0')} />}>
              <p>
                <b>Ligado:</b> quando a cega fecha com produto contado diferente da nota, o comprador é marcado com a lista (nota × contado). Só os produtos de cada nota, mesmo com a cega compartilhada.
              </p>
              <p>
                <b>Desligado:</b> a diferença fica só no roteiro da nota e na linha do tempo.
              </p>
            </Regra>

            <Regra titulo="Slack: transferência no destino sem chegada" controle={<div className="flex items-center gap-3"><CampoNumero valor={v('entradas.transf_parada_dias')} aoMudar={(x) => definir('entradas.transf_parada_dias', x)} sufixo="dias" /><Interruptor rotulo="Avisar transferência parada" ligado={ligado('entradas.slack_avisar_transf_parada')} aoMudar={(x) => definir('entradas.slack_avisar_transf_parada', x ? '1' : '0')} /></div>}>
              <p>
                <b>Ligado:</b> a NF de transferência já entrou no destino há mais dias do que o ao lado e não há cega nem chegada: a logística é avisada. Repete depois do mesmo prazo.
              </p>
              <p>
                <b>Desligado:</b> sem aviso; a nota continua Agendada.
              </p>
            </Regra>

            <Regra titulo="Slack: lembrete de aprovação sem resposta" controle={<div className="flex items-center gap-3"><CampoNumero valor={v('entradas.autorizacao_lembrete_h')} aoMudar={(x) => definir('entradas.autorizacao_lembrete_h', x)} sufixo="h" /><Interruptor rotulo="Lembrar aprovação" ligado={ligado('entradas.slack_lembrar_autorizacao')} aoMudar={(x) => definir('entradas.slack_lembrar_autorizacao', x ? '1' : '0')} /></div>}>
              <p>
                <b>Ligado:</b> pedido de aprovação (DOH ou agendamento com divergência) sem decisão há mais horas do que o ao lado: quem aprova DOH (e quem foi marcado) é lembrado na thread.
              </p>
              <p>
                <b>Desligado:</b> o pedido espera sem lembrete.
              </p>
            </Regra>

            <Regra titulo="Slack: perguntar a OC sozinho" controle={<div className="flex items-center gap-3"><CampoNumero valor={v('entradas.sem_oc_pergunta_h')} aoMudar={(x) => definir('entradas.sem_oc_pergunta_h', x)} sufixo="h" /><Interruptor rotulo="Perguntar a OC" ligado={ligado('entradas.slack_perguntar_oc_auto')} aoMudar={(x) => definir('entradas.slack_perguntar_oc_auto', x ? '1' : '0')} /></div>}>
              <p>
                <b>Ligado:</b> nota Sem OC há mais horas do que o ao lado: o WMS pergunta a OC aos compradores das OCs abertas do fornecedor, com os itens, as OCs candidatas e a DANFE. Uma vez por nota.
              </p>
              <p>
                <b>Desligado:</b> só pelo botão "Perguntar a OC ao comprador" da gaveta.
              </p>
            </Regra>
          </GrupoRegras>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Botao variante="primaria" disabled={!sujo} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            <Save className="h-4 w-4" /> Salvar regras
          </Botao>
          {salvar.isSuccess && !sujo ? <span className="text-xs font-semibold text-status-livre">Salvo.</span> : null}
          {salvar.error ? <span className="text-xs text-status-divergencia">{salvar.error instanceof ErroHttp ? salvar.error.message : String(salvar.error)}</span> : null}
        </div>
      </CorpoCartao>
    </Cartao>
  );
}

interface PessoaCanal {
  slackUserId: string;
  nome: string;
  email: string | null;
  usuario: number | null;
  comprador: number | null;
  aprovaDoh: boolean;
  origem: 'salvo' | 'automatico' | 'sem';
  como: 'email' | 'nome' | null;
}

interface RespostaPessoas {
  pessoas: PessoaCanal[];
  usuarios: { usuario: number; login: string; nome: string | null }[];
  compradores: { comprador: number; nome: string }[];
}

/**
 * Quem é quem no Slack, já preenchido. A conexão é do app (um bot para o WMS
 * todo); aqui só se confere a ligação de cada pessoa do canal com o usuário
 * dela no WMS — feita sozinha pelo e-mail do Slack = login do Procfit — e se
 * ela é comprador. Ninguém digita ID. Quem entrar no canal depois é ligado
 * sozinho na primeira resposta.
 */
function SlackUsuarios(): JSX.Element {
  const consulta = useQuery<RespostaPessoas>({ queryKey: ['entradas-slack-opcoes'], queryFn: () => api.get('/api/entradas/slack-pessoas') });
  const [edicao, definirEdicao] = React.useState<Record<string, { usuario: number | null; comprador: number | null; aprovaDoh?: boolean }>>({});
  const salvar = useMutation({
    mutationFn: () =>
      api.put('/api/entradas/slack-pessoas', {
        pessoas: (consulta.data?.pessoas ?? []).map((p) => ({
          slackUserId: p.slackUserId,
          nome: p.nome,
          usuario: edicao[p.slackUserId]?.usuario ?? p.usuario,
          comprador: edicao[p.slackUserId]?.comprador ?? p.comprador,
          aprovaDoh: edicao[p.slackUserId]?.aprovaDoh ?? p.aprovaDoh,
        })),
      }),
    onSuccess: () => {
      definirEdicao({});
      void consulta.refetch();
    },
  });

  const pessoas = consulta.data?.pessoas ?? [];
  const pendentes = pessoas.filter((p) => p.origem !== 'salvo').length;
  const seletor = 'h-9 w-full rounded-lg border border-borda bg-superficie px-2 text-xs';
  const valor = (p: PessoaCanal, campo: 'usuario' | 'comprador'): string => String(edicao[p.slackUserId]?.[campo] ?? p[campo] ?? '');
  const mudar = (p: PessoaCanal, campo: 'usuario' | 'comprador', v: string): void =>
    definirEdicao((e) => ({
      ...e,
      [p.slackUserId]: {
        usuario: e[p.slackUserId]?.usuario ?? p.usuario,
        comprador: e[p.slackUserId]?.comprador ?? p.comprador,
        aprovaDoh: e[p.slackUserId]?.aprovaDoh ?? p.aprovaDoh,
        [campo]: v ? Number(v) : null,
      },
    }));
  const aprova = (p: PessoaCanal): boolean => edicao[p.slackUserId]?.aprovaDoh ?? p.aprovaDoh;
  const mudarAprova = (p: PessoaCanal, v: boolean): void =>
    definirEdicao((e) => ({
      ...e,
      [p.slackUserId]: {
        usuario: e[p.slackUserId]?.usuario ?? p.usuario,
        comprador: e[p.slackUserId]?.comprador ?? p.comprador,
        aprovaDoh: v,
      },
    }));

  return (
    <Cartao>
      <CorpoCartao className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-sm font-bold">Pessoas do canal</p>
            <p className="text-xs text-textoFraco">
              O jeito mais simples: cada pessoa clica em <b>Conectar meu Slack</b> no topo da tela Entradas. Aqui aparece quem está no canal,
              já ligado pelo e-mail do Slack = login do Procfit (paulo.silva@… → paulo.silva) — só confira quem vier marcado “confira”.
            </p>
          </div>
          {pendentes ? <Pilula tom="alerta">{pendentes} para conferir</Pilula> : pessoas.length ? <Pilula tom="ok">Tudo conferido</Pilula> : null}
        </div>

        {consulta.isLoading ? <p className="text-xs text-textoSuave">Lendo o canal no Slack…</p> : null}
        {consulta.error ? <p className="text-xs text-status-divergencia">{consulta.error instanceof ErroHttp ? consulta.error.message : String(consulta.error)}</p> : null}
        {!consulta.isLoading && !pessoas.length && !consulta.error ? (
          <p className="text-xs text-textoSuave">Ninguém no canal ainda (além do bot). Adicione as pessoas no Slack e volte aqui.</p>
        ) : null}

        {pessoas.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-textoFraco">
                <tr>
                  <th className="py-1.5 pr-2">No Slack</th>
                  <th className="py-1.5 pr-2">Usuário do WMS</th>
                  <th className="py-1.5 pr-2">Comprador (ERP)</th>
                  <th className="py-1.5" title="Pode aprovar no Slack o agendamento acima do DOH">
                    Aprova DOH
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borda">
                {pessoas.map((p) => (
                  <tr key={p.slackUserId}>
                    <td className="py-2 pr-2 align-top">
                      <b className="block">{p.nome}</b>
                      <span className="text-textoSuave">{p.email ?? 'sem e-mail'}</span>
                      <span className="mt-0.5 block">
                        {p.origem === 'salvo' ? (
                          <Pilula tom="ok">conferido</Pilula>
                        ) : p.como === 'email' ? (
                          <Pilula tom="marca">achado pelo e-mail</Pilula>
                        ) : p.origem === 'automatico' ? (
                          <Pilula tom="alerta">pelo nome — confira</Pilula>
                        ) : (
                          <Pilula tom="neutro">não achado</Pilula>
                        )}
                      </span>
                    </td>
                    <td className="py-2 pr-2 align-top">
                      <select className={seletor} value={valor(p, 'usuario')} onChange={(e) => mudar(p, 'usuario', e.target.value)}>
                        <option value="">— sem usuário (resposta vira comentário)</option>
                        {(consulta.data?.usuarios ?? []).map((u) => (
                          <option key={u.usuario} value={u.usuario}>
                            {u.nome ?? u.login} ({u.login})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <select className={seletor} value={valor(p, 'comprador')} onChange={(e) => mudar(p, 'comprador', e.target.value)}>
                        <option value="">— não é comprador</option>
                        {(consulta.data?.compradores ?? []).map((c) => (
                          <option key={c.comprador} value={c.comprador}>
                            {c.nome}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pl-2 text-center align-top">
                      <input
                        type="checkbox"
                        className="mt-2 h-4 w-4 accent-[rgb(var(--cor-primaria))]"
                        aria-label={`${p.nome} aprova agendamento fora do DOH`}
                        checked={aprova(p)}
                        onChange={(e) => mudarAprova(p, e.target.checked)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {salvar.error ? <p className="text-xs text-status-divergencia">{salvar.error instanceof ErroHttp ? salvar.error.message : String(salvar.error)}</p> : null}
        {pessoas.length ? (
          <Botao variante="primaria" carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            <Save className="h-4 w-4" /> {pendentes ? 'Confirmar ligações' : 'Salvar alterações'}
          </Botao>
        ) : null}
      </CorpoCartao>
    </Cartao>
  );
}

interface StatusSlack {
  conectado: boolean;
  motivo?: string;
  workspace?: string;
  bot?: string;
  canal: string | null;
  canaisSeparados: boolean;
}

interface CanalSlack {
  id: string;
  nome: string;
  privado: boolean;
  botNoCanal: boolean;
  membros: number | null;
}

/**
 * A conexão com o Slack num cartão só: está conectado? em qual canal? manda
 * um teste. O token fica no servidor (variável do EB), não no banco — por
 * isso "conectar" aqui é conferir e escolher o canal, não colar chave.
 */
function ConexaoSlack(): JSX.Element {
  const cliente = useQueryClient();
  const status = useQuery<StatusSlack>({ queryKey: ['entradas-slack-status'], queryFn: () => api.get('/api/entradas/slack-status') });
  const canais = useQuery<{ canais: CanalSlack[] }>({
    queryKey: ['entradas-slack-canais'],
    queryFn: () => api.get('/api/entradas/slack-canais'),
    enabled: Boolean(status.data?.conectado),
  });
  const [escolhido, definirEscolhido] = React.useState('');
  React.useEffect(() => {
    if (status.data?.canal) definirEscolhido(status.data.canal);
  }, [status.data?.canal]);

  const salvarCanal = useMutation({
    mutationFn: () => api.put<{ nome: string }>('/api/entradas/slack-canal', { canal: escolhido }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['entradas-slack-status'] });
      void cliente.invalidateQueries({ queryKey: ['entradas-slack-canais'] });
      void cliente.invalidateQueries({ queryKey: ['entradas-slack-opcoes'] });
    },
  });
  const teste = useMutation({ mutationFn: () => api.post('/api/entradas/slack-teste') });

  const s = status.data;
  const atual = canais.data?.canais.find((c) => c.id === s?.canal);
  const erro = (e: unknown): string | null => (e instanceof ErroHttp ? e.message : e ? String(e) : null);

  return (
    <Cartao>
      <CorpoCartao className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-bold">Conexão com o Slack</p>
            <p className="text-xs text-textoFraco">
              Um bot só para o WMS inteiro (não depende de quem está logado): abre uma thread por nota no canal escolhido, marca quem precisa agir e lê as respostas (`oc 12345`, `revalidar`, `recusar …`).
            </p>
          </div>
          {status.isLoading ? null : s?.conectado ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-status-livreFundo px-3 py-1 text-xs font-bold text-status-livre">
              <CheckCircle2 className="h-4 w-4" /> Conectado como @{s.bot} · {s.workspace}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-status-divergenciaFundo px-3 py-1 text-xs font-bold text-status-divergencia">
              <XCircle className="h-4 w-4" /> Não conectado
            </span>
          )}
        </div>

        {s && !s.conectado ? (
          <p className="rounded-lg border border-borda bg-superficie2 px-3 py-2 text-[13px] text-textoFraco">
            {s.motivo} A TI coloca o token do app <b>WMS Entradas</b> nas variáveis do servidor e reinicia o WMS.
          </p>
        ) : null}

        {s?.conectado ? (
          <div className="grid gap-3 lg:grid-cols-[1fr_auto_auto] lg:items-end">
            <label className="block space-y-1">
              <span className="text-xs font-semibold">Canal da esteira</span>
              <select
                className="min-h-[42px] w-full rounded-xl border border-borda bg-superficie px-3 text-[13px]"
                value={escolhido}
                onChange={(e) => definirEscolhido(e.target.value)}
                disabled={canais.isLoading}
              >
                <option value="">{canais.isLoading ? 'Carregando canais…' : 'Escolha um canal'}</option>
                {(canais.data?.canais ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.privado ? '🔒 ' : '# '}
                    {c.nome}
                    {c.botNoCanal ? ' · bot já está' : c.privado ? ' · convide o bot' : ''}
                    {c.membros != null ? ` · ${c.membros} membros` : ''}
                  </option>
                ))}
              </select>
            </label>
            <Botao variante="primaria" disabled={!escolhido || escolhido === s.canal} carregando={salvarCanal.isPending} onClick={() => salvarCanal.mutate()}>
              <Save className="h-4 w-4" /> Usar este canal
            </Botao>
            <Botao variante="secundaria" disabled={!s.canal} carregando={teste.isPending} onClick={() => teste.mutate()}>
              <Send className="h-4 w-4" /> Enviar teste
            </Botao>
          </div>
        ) : null}

        {s?.conectado && s.canal ? (
          <p className="flex flex-wrap items-center gap-1.5 text-xs text-textoFraco">
            Em uso:
            <b className="inline-flex items-center gap-1 text-texto">
              {atual?.privado ? <Lock className="h-3 w-3" /> : <Hash className="h-3 w-3" />}
              {atual?.nome ?? s.canal}
            </b>
            para compras, fiscal e logística — cada nota tem a própria thread.
            {s.canaisSeparados ? ' (Hoje há canais diferentes por etapa; salvar aqui unifica.)' : ''}
          </p>
        ) : null}
        {salvarCanal.isSuccess ? <p className="text-xs text-status-livre">Canal salvo.</p> : null}
        {teste.isSuccess ? <p className="text-xs text-status-livre">Mensagem de teste enviada — confira no canal.</p> : null}
        {erro(salvarCanal.error) || erro(teste.error) || erro(canais.error) ? (
          <p className="text-xs text-status-divergencia">{erro(salvarCanal.error) ?? erro(teste.error) ?? erro(canais.error)}</p>
        ) : null}
      </CorpoCartao>
    </Cartao>
  );
}

/**
 * A tributação que a NF de transferência usa, de leitura: por grupo tributário
 * transferido nos últimos 120 dias, a regra de hoje no PBS (o que o WMS vai
 * aplicar) ao lado do que a última NF real usou. Diferença = o fiscal decide
 * se a regra ou a NF estava certa; a mudança é no PBS (Grupos Tributários),
 * para o WMS e o Procfit nunca divergirem. Só carrega quando aberta.
 */
function TributacaoTransferencia(): JSX.Element {
  const [aberta, definirAberta] = React.useState(false);
  const [soDiferentes, definirSoDiferentes] = React.useState(true);
  const [empresa, definirEmpresa] = React.useState('11');
  const consulta = useQuery<{ linhas: LinhaTributacao[] }>({
    queryKey: ['entradas-transf-tributacao'],
    queryFn: () => api.get('/api/entradas/transferencia-nf/tributacao'),
    enabled: aberta,
    staleTime: 10 * 60_000,
  });
  const linhas = consulta.data?.linhas ?? [];
  const empresas = [...new Set(linhas.map((l) => l.empresa))].sort((a, b) => a - b);
  const difere = (l: LinhaTributacao): boolean => l.cst == null || l.cst !== l.ultimaCst || Number(l.aliquota) !== Number(l.ultimaAliquota);
  const daEmpresa = linhas.filter((l) => !empresa || String(l.empresa) === empresa);
  const diferentes = daEmpresa.filter(difere);
  const visiveis = soDiferentes ? diferentes : daEmpresa;
  const pct = (v: number | null): string => (v == null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`);

  return (
    <Cartao className="overflow-hidden">
      <button
        type="button"
        aria-expanded={aberta}
        className="flex w-full items-start justify-between gap-2 px-4 py-3 text-left hover:bg-superficie2"
        onClick={() => definirAberta((x) => !x)}
      >
        <div>
          <p className="text-sm font-bold">Tributação da NF de transferência</p>
          <p className="text-xs text-textoFraco">
            CST e alíquota de ICMS vêm do grupo tributário de cada produto, para a operação de saída e as UFs de origem e destino — a mesma regra do
            Procfit. Aqui você confere a regra de hoje contra a última NF real.
          </p>
        </div>
        <span className="shrink-0 text-xs font-semibold text-primaria">{aberta ? 'Fechar' : 'Conferir'}</span>
      </button>
      {aberta ? (
        consulta.isLoading ? (
          <p className="px-4 pb-4 text-xs text-textoFraco">Lendo as transferências dos últimos 120 dias…</p>
        ) : consulta.isError ? (
          <p className="px-4 pb-4 text-xs text-status-divergencia">{consulta.error instanceof ErroHttp ? consulta.error.message : 'Não deu para ler.'}</p>
        ) : (
          <div className="space-y-2 border-t border-borda px-4 py-3">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <label className="flex items-center gap-1.5">
                Empresa de origem
                <select className="h-8 rounded-md border border-borda bg-superficie px-1.5" value={empresa} onChange={(e) => definirEmpresa(e.target.value)}>
                  <option value="">todas</option>
                  {empresas.map((e) => (
                    <option key={e} value={String(e)}>
                      {e}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={soDiferentes} onChange={(e) => definirSoDiferentes(e.target.checked)} />
                Só onde a regra de hoje difere da última NF ({diferentes.length})
              </label>
              <span className="text-textoSuave">
                {daEmpresa.length} grupo(s) · {daEmpresa.length - diferentes.length} batem
              </span>
            </div>
            {visiveis.length ? (
              <div className="max-h-[420px] overflow-auto rounded-lg border border-borda">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-superficie2 text-left text-textoFraco">
                    <tr>
                      <th className="px-2 py-1.5">Grupo tributário</th>
                      <th className="px-2 py-1.5">Origem → destino</th>
                      <th className="px-2 py-1.5">Regra hoje (o WMS usa)</th>
                      <th className="px-2 py-1.5">Última NF</th>
                      <th className="px-2 py-1.5 text-right">Itens / produtos</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borda">
                    {visiveis.map((l) => (
                      <tr key={`${l.empresa}-${l.operacao}-${l.ufDestino}-${l.grupo}`} className={cn(difere(l) && 'bg-status-atencaoFundo/40')}>
                        <td className="px-2 py-1.5">
                          <b className="tabular-nums">{l.grupo ?? '—'}</b> <span className="text-textoFraco">{l.descricao}</span>
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5">
                          {l.empresa} {l.ufOrigem} → {l.ufDestino} · op. {l.operacao}
                        </td>
                        <td className={cn('whitespace-nowrap px-2 py-1.5 font-semibold', l.cst == null && 'text-status-divergencia')}>
                          {l.cst == null ? 'sem regra: o WMS não emite' : `CST ${l.cst} · ${pct(l.aliquota)}`}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5">
                          CST {l.ultimaCst ?? '—'} · {pct(l.ultimaAliquota)}
                          <span className="block text-textoSuave">
                            NF {l.ultimaNf ?? '—'}
                            {l.ultimaEmissao ? ` · ${new Date(l.ultimaEmissao).toLocaleDateString('pt-BR')}` : ''}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {l.itens} / {l.produtos}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="rounded-lg bg-status-livreFundo px-3 py-2 text-xs text-status-livre">Todas as regras de hoje batem com a última NF real.</p>
            )}
            <p className="text-2xs text-textoSuave">Para mudar uma regra, o fiscal altera o grupo tributário no Procfit; o WMS lê na hora, sem deploy.</p>
          </div>
        )
      ) : null}
    </Cartao>
  );
}
